import React from 'react';
import { Box, Divider } from '@withjoy/joykit';
import { useNativeState } from '@shared/core/native';
import { useCatalogRegistryTranslations } from './Catalog.i18n';
import { useCatalogController } from './Catalog.controller';
import { Redirect, Route, Switch } from '@react-router';
import { NotificationToast } from '@apps/registry/common/components/NotificationToast';
import { RegistryFontFaces } from '@apps/registry/common/util/fonts';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import loadable from '@loadable/component';
import { RegistryCurrencyCodesFragment } from '@graphql/generated';
import { useFeatureValue } from '@shared/core/featureFlags';
import { BackToTop } from './components/BackToTop';
import { MegaNav } from './components/MegaNav';
import { ScrollToTop } from '@apps/common/components/ScrollToTop/ScrollToTop';
import { MarcomAppFooter } from '@shared/components/Marcom/AppFooter';
import { pxToRem } from '@withjoy/joykit/theme';
import { useRouterHelper } from '@shared/core';
import { DESKTOP_NAV_HEIGHT } from '@shared/components/Marcom/AppNavBar/AppNavBar.constants';

const CatalogProductDetails = loadable(
  () =>
    import(
      /* webpackChunkName: "content/catalog/productdetails" */
      '@apps/registry/common/components/Catalog/routes/CatalogProductDetails'
    )
);

const CurationDetails = loadable(
  () =>
    import(
      /* webpackChunkName: "content/catalog/curationdetails" */
      '@apps/registry/common/components/Catalog/routes/CurationDetails'
    )
);
const CatalogProductsIndex = loadable(
  () =>
    import(
      /* webpackChunkName: "content/catalog/productsindex" */
      '@apps/registry/common/components/Catalog/routes/CatalogProductsIndex'
    )
);
const CurationsIndex = loadable(
  () =>
    import(
      /* webpackChunkName: "content/catalog/curationsindex" */
      '@apps/registry/common/components/Catalog/routes/CurationsIndex'
    )
);

const BrandPage = loadable(
  () =>
    import(
      /* webpackChunkName: "content/catalog/brandpage" */
      '@apps/registry/common/components/Catalog/routes/BrandPage'
    )
);

const BrandsList = loadable(
  () =>
    import(
      /* webpackChunkName: "content/catalog/brandslist" */
      '@apps/registry/common/components/Catalog/routes/BrandsList'
    )
);

const CatalogShippingPolicy = loadable(
  () =>
    import(
      /* webpackChunkName: "content/catalog/shippingpolicy" */
      '@apps/registry/common/components/Catalog/routes/ShippingPolicy'
    )
);

const CatalogReturnPolicy = loadable(
  () =>
    import(
      /* webpackChunkName: "content/catalog/ReturnPolicy" */
      '@apps/registry/common/components/Catalog/routes/ReturnPolicy'
    )
);

interface CatalogProps {
  heroContent?: React.ReactNode;
  registryCurrency?: RegistryCurrencyCodesFragment | null;
}

const Catalog: React.FC<CatalogProps> = ({ heroContent, registryCurrency }) => {
  const { isMobile, notificationVisible, setNotificationVisible, getRootCatalogPath } = useCatalogController();

  const { getAddProductTranslations } = useCatalogRegistryTranslations();
  const { notificationText } = getAddProductTranslations();
  const isCurationHomeExpertFeatureEnabled = useFeatureValue('enableCurationHomeExpert');
  const { loading: loadingCatalogBrandsDirectoryFf, value: catalogBrandsDirectoryEnabled } = useFeatureValue('catalogBrandsDirectoryEnabled');
  const { value: shopProductConfirmationToastV2Enabled } = useFeatureValue('shopProductConfirmationToastV2');

  const { catalogRoutes } = useCatalogRoutePaths();

  return (
    <>
      <Switch>
        <Route
          path={catalogRoutes.shippingPolicy.paths}
          render={() => {
            return <CatalogShippingPolicy />;
          }}
        />
        <Route
          path={catalogRoutes.returnPolicy.paths}
          render={() => {
            return <CatalogReturnPolicy />;
          }}
        />
        <Route
          path={catalogRoutes.curationDetails.paths}
          render={({ match, staticContext }) => {
            return match.params.elementId ? (
              <>
                <CurationDetails curationLocator={match.params.elementId} isMobile={isMobile} staticContext={staticContext} />
                <BackToTop />
              </>
            ) : (
              <Redirect to="/shop" />
            );
          }}
        />
        <Route
          path={catalogRoutes.curationsIndex.paths}
          render={() => {
            return <CurationsIndex />;
          }}
        />
        <Route
          path={catalogRoutes.brandsList.paths}
          exact={true}
          render={() => (loadingCatalogBrandsDirectoryFf || catalogBrandsDirectoryEnabled === 'on' ? <BrandsList /> : <Redirect to="/shop" />)}
        />
        <Route
          path={catalogRoutes.brandPage.paths}
          render={({ match }) => {
            return match.params.elementId ? (
              <>
                <BrandPage brandLocator={match.params.elementId} isMobile={isMobile} />
                <BackToTop />
              </>
            ) : (
              <Redirect to="/shop" />
            );
          }}
        />
        <Route
          path={[...catalogRoutes.catalogIndex.paths, ...catalogRoutes.recommendedProducts.paths]}
          exact={true}
          render={() => {
            return (
              <>
                <CatalogProductsIndex categoryLocator={null} heroContent={isCurationHomeExpertFeatureEnabled ? <></> : heroContent} />

                <BackToTop />
              </>
            );
          }}
        />
        <Route
          path={[...catalogRoutes.catalogCategory.paths]}
          render={({ match, staticContext }) => {
            return (
              <>
                <CatalogProductsIndex
                  staticContext={staticContext}
                  categoryLocator={match.params.elementId}
                  heroContent={isCurationHomeExpertFeatureEnabled ? <></> : heroContent}
                />

                <BackToTop />
              </>
            );
          }}
        />

        {/* Redirect to shop root if no valid route matches */}
        <Redirect to={getRootCatalogPath()} />
      </Switch>
      {shopProductConfirmationToastV2Enabled === 'on' ? null : (
        <NotificationToast notificationText={notificationText} notificationVisible={notificationVisible} setNotificationVisible={setNotificationVisible} />
      )}
    </>
  );
};
Catalog.displayName = 'Catalog';

interface CatalogContainerProps {
  heroContent?: React.ReactNode;
  header?: React.ReactNode;
  registryCurrency?: RegistryCurrencyCodesFragment | null;
  stickyNav?: boolean;
}

const CatalogContainer: React.FC<CatalogContainerProps> = props => {
  const { isNative } = useNativeState();
  const { catalogRoutes } = useCatalogRoutePaths();
  const { isAdminRoute } = useRouterHelper();

  return (
    <>
      <RegistryFontFaces />
      {props.header}
      <Box position="relative" zIndex={3}>
        <MegaNav />
      </Box>
      <Divider top={[pxToRem(66 + (isNative ? 52 : 0)), null, pxToRem(DESKTOP_NAV_HEIGHT)]} zIndex={6} position={props.stickyNav ? 'sticky' : 'initial'} />
      <Switch>
        <Route
          path={catalogRoutes.catalogItem.paths}
          render={({ match, staticContext }) => {
            return match && match.params.elementId ? (
              <>
                <ScrollToTop behavior="instant" />
                <CatalogProductDetails catalogItemId={match.params.elementId} staticContext={staticContext} />
              </>
            ) : (
              <Redirect to="/shop" />
            );
          }}
        />
        <Route
          render={() => {
            return <Catalog {...props} />;
          }}
        />
      </Switch>
      {!isNative && <MarcomAppFooter hidePrimaryFooter={isAdminRoute} />}
    </>
  );
};

export { CatalogContainer };
